import styled from '@emotion/styled';
import { FileReference, FileReferenceId } from '@kaizenplatform/belfast-entity';
import { FileIdsValue, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DynamicSchemaDefinition,
  DynamicSchemaDefinitionField,
  DynamicSchemaDefinitionFileIds,
} from '../../../generated/graphql';
import { filterBlankAssetCategorySectionsField } from '../../../graphql/dynamic_schema';
import { EnhancedBatchDownload } from '../../connected/EnhancedBatchDownload';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import Field from './AssetCategoryDetail/Field/Field';

type Props = {
  className?: string;
  definition: DynamicSchemaDefinition;
  assetFiles: ReadonlyArray<FileReference>;
  latestAssetValue: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
  beforeAssetValue?: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null> | null;
  assetCategorySections: Array<{
    fields: Array<Pick<DynamicSchemaDefinitionField, 'key' | 'type'> | Record<string, never>>;
  }> | null;
};

const isFileChanged = (
  definition: DynamicSchemaDefinition,
  beforeAssetValue: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>,
  latestAssetValue: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>,
): boolean => {
  const beforeFiles = definition.sections.reduce((acc: Array<FileReferenceId>, cur) => {
    const fileIdsField = cur.fields.filter(
      (field): field is DynamicSchemaDefinitionFileIds => field.type === 'FILE_IDS',
    )[0];
    const files = beforeAssetValue.get(fileIdsField.key) as Array<FileReferenceId>;
    return [...acc, ...files];
  }, []);

  const latestFiles = definition.sections.reduce((acc: Array<FileReferenceId>, cur) => {
    const fileIdsField = cur.fields.filter(
      (field): field is DynamicSchemaDefinitionFileIds => field.type === 'FILE_IDS',
    )[0];
    const files = latestAssetValue.get(fileIdsField.key) as Array<FileReferenceId>;
    return [...acc, ...files];
  }, []);

  return beforeFiles.toString() !== latestFiles.toString();
};

const ProjectBasicSettingDetailDiff = ({
  className,
  definition,
  assetFiles,
  latestAssetValue,
  beforeAssetValue,
  assetCategorySections,
}: Props & ClassAttributes<HTMLElement>) => {
  const downloadFileIds =
    assetCategorySections != null
      ? filterBlankAssetCategorySectionsField(assetCategorySections.flatMap((s) => s.fields))
          .filter((field): field is DynamicSchemaDefinitionFileIds => field.type === 'FILE_IDS')
          .flatMap((field) => latestAssetValue.get(field.key) as ReadonlyArray<string>)
      : [];

  return (
    <Root className={className}>
      <Section>
        <SectionHeading variant="h6">
          <FormattedMessage id="project_basic_setting_detail.section_heading.assets" />
          <WarningText>
            {beforeAssetValue != null &&
            latestAssetValue != null &&
            isFileChanged(definition, beforeAssetValue, latestAssetValue) ? (
              <FormattedMessage id="project_basic_setting_detail.assets.uploaded_files.changed" />
            ) : null}
          </WarningText>
        </SectionHeading>

        <LabelAndValue>
          <Label>
            <FormattedMessage id="project_basic_setting_detail.assets.label.uploaded_files" />
          </Label>
          <Value>
            {latestAssetValue != null &&
              definition.sections.map((section) => {
                return (
                  <Fragment key={section.id}>
                    <Typography variant="h6">{section.label}</Typography>
                    {section.fields.map((field) => (
                      <Field
                        key={field.key}
                        field={field}
                        value={latestAssetValue}
                        beforeValue={beforeAssetValue != null ? beforeAssetValue : undefined}
                        files={assetFiles}
                        meta={section.meta}
                      />
                    ))}
                  </Fragment>
                );
              })}
            {assetFiles.length >= 2 ? <EnhancedBatchDownload fileIds={downloadFileIds} /> : null}
          </Value>
        </LabelAndValue>
      </Section>
    </Root>
  );
};

const Root = styled('div')({});

const Section = styled('div')({
  marginBottom: SPACING_UNIT * 3,
});

const SectionHeading = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});
SectionHeading.defaultProps = { variant: 'h6' };

const WarningText = styled('div')({
  fontSize: '14px',
  color: 'red',
});
const LabelAndValue = styled('div')({
  ...gridLayoutContainer({
    areas: [['label', '.', 'value']],
    columns: ['20%', `${SPACING_UNIT * 3}px`, `calc(100% - 20% - ${SPACING_UNIT * 3}px)`],
    rows: ['auto'],
  }),
  marginBottom: SPACING_UNIT,
});

const Label = styled(Typography)({
  ...gridLayoutItem('label'),
  textAlign: 'end',
});

const Value = styled('div')({
  ...gridLayoutItem('value'),
});

export default ProjectBasicSettingDetailDiff;
